import React, { useState, useEffect, useContext } from "react";
import { PropagateLoader } from 'react-spinners';
import { motion, AnimatePresence } from 'framer-motion';
import { IoChevronDown, IoChevronUp, IoAdd, IoCallOutline, IoMenu, IoClose } from "react-icons/io5";
import ContactModal from "../contact/contactModal/ContactModal";
import { AuthContext } from "../../utils/context/auth"; 
import { FilterProvider } from '../../utils/context/FilterContext';
import DashboardTour from "./DashboardTour";
import SalesRepDashboard from "./salesrep/SalesRepDashboard";
import AdvancedCareDashboard from "./advancedcare/AdvancedCareDashboard";
import axiosAuth from "../../utils/axios";
import RequestReviewModal from "./patient/RequestReviewModal";
import ShippingDashboard from "./shipping/ShippingDashboard";
import PendingOrdersAdminDashboard from "./admin/PendingOrdersAdminDashboard";
import SalesRepAdvancedCareDashboard from "./salesrep/SalesRepAdvancedCareDashboard";
import BillerDashboard from "./biller/BillerDashboard";
import CompressionSockBillerView from "./biller/CompressionSockBillerView";
const SPLASH_DURATION = 1000; 
const FADE_OUT_TIME = 500;  

const API_BASE_URL = "https://promedhealth-frontdoor-h4c4bkcxfkduezec.z02.azurefd.net/api/v1/";
 

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const [openModal, setOpenModal] = useState(false);
  const [showSplash, setShowSplash] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [nextService, setNextService] = useState(null);
  const [reorderData, setReorderData] = useState(null);
  // ✅ Sales rep access state
  const [salesRepHasAccess, setSalesRepHasAccess] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [billerSection, setBillerSection] = useState("guided");
  const [reviewRequestModalOpen, setReviewRequestModalOpen] = useState(false);
  const [selectedPatientForReview, setSelectedPatientForReview] = useState(null);
  const [readyToCompleteCount, setReadyToCompleteCount] = useState(0);
  // Get initial service from localStorage
  const initialService = localStorage.getItem('selectedTreatment') || "skin_subs";
  const [selectedService, setSelectedService] = useState(initialService);

  const [providerReorderData, setProviderReorderData] = useState(null);
  
  // Light mode only - Always remove dark class
  useEffect(() => {
    document.documentElement.classList.remove("dark");
    localStorage.setItem("darkMode", "false");
  }, []);

  // Navigation state
   // ✅ Start with null to show sales rep dashboard
  const [isPatientsDropdownOpen, setIsPatientsDropdownOpen] = useState(false);
  const [isSupportDropdownOpen, setIsSupportDropdownOpen] = useState(false);
  
  // Mobile sidebar state
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  const userRole = localStorage.getItem("userRole");
  const userEmail = localStorage.getItem("userEmail");
  const [currentNavItem, setCurrentNavItem] = useState(
  userRole === "provider" ? 'patients' : null
);

  // ✅ Check if sales rep has access from ANY provider
  useEffect(() => {
    const checkSalesRepAccess = async () => {
      if (userRole !== "sales_rep") {
        setCheckingAccess(false);
        return;
      }

      try {
        const axiosInstance = axiosAuth();
        const response = await axiosInstance.get('/sales-rep/check-access/');
        
        if (response.data.success) {
          console.log(response.data.has_access);
          setSalesRepHasAccess(response.data.has_access);
        } else {
          setSalesRepHasAccess(false);
        }
      } catch (error) {
        console.error("❌ Failed to check sales rep access:", error);
        setSalesRepHasAccess(false);
      } finally {
        setCheckingAccess(false);
      }
    };

    if (userRole === "sales_rep") {
      checkSalesRepAccess();
    } else {
      setCheckingAccess(false);
    }
  }, [userRole]);

  useEffect(() => {
    const fadeTimer = setTimeout(() => {
      setFadeOut(true);
    }, SPLASH_DURATION);
    const hideTimer = setTimeout(() => {
      setShowSplash(false);
    }, SPLASH_DURATION + FADE_OUT_TIME); 
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(hideTimer);
    };
  }, []); 

  // Add this in Dashboard.js alongside other useEffects
  useEffect(() => {
    if (userRole !== 'provider') return;
    
    const fetchReadyCount = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const res = await fetch(`${API_BASE_URL}/pending-ivr/`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        console.log(data)
        if (data.success) {
          const count = (data.data || []).filter(o => o.can_complete).length;
          setReadyToCompleteCount(count);
        }
      } catch (e) {}
    };

    fetchReadyCount();
    const interval = setInterval(fetchReadyCount, 30000);
    return () => clearInterval(interval);
  }, [userRole]);

  // Handle service selection from dropdown
  // Handle service selection from dropdown
  const handleServiceSelect = (serviceId) => {
    setSelectedService(serviceId);
    localStorage.setItem('selectedTreatment', serviceId);
    
    // ✅ Different behavior for sales reps vs providers
    if (userRole === "sales_rep") {
      setCurrentNavItem('patients'); // Sales reps need this to show the service view
    } else {
      setCurrentNavItem(null); // Providers keep original behavior
    }
  };

   const handleOpenReviewRequest = (patient) => {
    setSelectedPatientForReview(patient);
    setReviewRequestModalOpen(true);
  };

  // ✅ Handler to close modal
  const handleCloseReviewRequest = () => {
    setReviewRequestModalOpen(false);
    setSelectedPatientForReview(null);
  };

  const handleReviewRequestSuccess = (data) => {
  console.log('Review request submitted:', data);
  // Optionally reload data or show notification
};

  if (!user) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-100 dark:bg-gray-900">
        <PropagateLoader color={"#10B981"} loading={true} size={15} />
      </div>
    );
  }

  if (showSplash) {
    return (
      <div 
        className={`
          fixed inset-0 
          bg-gray-900 
          z-50 
          flex flex-col items-center justify-center 
          transition-opacity duration-500 ease-in-out
          ${fadeOut ? 'opacity-0' : 'opacity-100'}
        `}
      >
        <div className="text-white text-3xl font-extrabold flex flex-col items-center">
          <h1 className="text-5xl font-bold mb-8 drop-shadow-lg text-white">
            ProMed Health <span className="text-teal-500">Plus</span>
          </h1>
          <div className="mb-10">
            <PropagateLoader 
              color={"#FFFFFF"} 
              loading={true}
              size={15}
              margin={5}
              speedMultiplier={0.8}
            />
          </div>
          <span className="tracking-wider text-xl text-white">Creating Your Dashboard...</span>
        </div>
      </div>
    );
  }

  

  // ✅ Determine if left navbar should be shown
  const shouldShowLeftNav = userRole === "provider" || (userRole === "sales_rep" && salesRepHasAccess) || (userRole === "external_biller" && userEmail !== "vbj@promedhealthplus.com"); 

  // Map nav items to dashboard views
  const getDashboardView = () => {
    switch(currentNavItem) {
      case 'patients':
        return 'patientsList';
      case 'pendingPartialOrders':
        return 'pendingPartialOrders';
      case 'addPatient':
        return 'newOrder';
      case 'accountSummary':
        return 'accountSummary';
      case 'reimbursementGuide':
        return 'reimbursementGuide';
      case 'faqs':
        return 'faqs';
      case null:
      case undefined:
      default:
        return 'dashboard';
    }
  };
console.log("Rendering main content for role:", userRole);
    console.log(userEmail);
  // ✅ UPDATED: Render content based on role and navigation
  const renderMainContent = () => {
  if (userRole === "admin" && userEmail === 'Katy@promedhealthplus.com') {
    return <ShippingDashboard />;
  }
  if (userRole === "admin" && userEmail === 'Manohar@promedhealthplus.com') {
    return <PendingOrdersAdminDashboard />;
  }
  if (userRole === "socks_biller") {
    return <CompressionSockBillerView />;
  }
  if (userRole === "external_biller") {
      return billerSection === "guided"
        ? <BillerDashboard />
        : <CompressionSockBillerView />;
  }
  
  // For providers, always show AdvancedCareDashboard
  if (userRole === "provider") {
    const dashboardView = getDashboardView();
    switch(selectedService) {
      case "skin_subs":
        return <AdvancedCareDashboard 
          source="skin_subs" 
          onSwitchToConservativeCare={() => setSelectedService("conservative_care")}
          initialView={dashboardView}
          currentNavItem={currentNavItem}
          onContactModalOpen={handleOpenReviewRequest}
          onNavigateToPatients={() => setCurrentNavItem('patients')}
          providerReorderData={providerReorderData}
          onProviderReorderComplete={() => setProviderReorderData(null)}
        />;
      
      case "conservative_care":
        return <AdvancedCareDashboard 
          source="conservative_care"
          initialView={dashboardView}
          currentNavItem={currentNavItem}
          onContactModalOpen={handleOpenReviewRequest}
          onNavigateToPatients={() => setCurrentNavItem('patients')}
          providerReorderData={providerReorderData}
          onProviderReorderComplete={() => setProviderReorderData(null)}
        />;
      
      default:
        return null;
    }
  }
  
  // ✅ For sales reps with access
  if (userRole === "sales_rep" && salesRepHasAccess) {
    // ✅ NEW: If adding patient (including reorder), use AdvancedCareDashboard
    // console.log("currentnavitem:",currentNavItem);
    if (currentNavItem === 'addPatient') {
      switch(selectedService) {
        case "skin_subs":
          return <AdvancedCareDashboard 
            source="skin_subs" 
            onSwitchToConservativeCare={() => setSelectedService("conservative_care")}
            initialView={reorderData ? 'newOrder' : 'providerSelection'} // ✅ Start at newOrder if reorder
            currentNavItem={currentNavItem}
            onContactModalOpen={handleOpenReviewRequest}
            onNavigateToPatients={() => {
              setCurrentNavItem('patients');
              setReorderData(null); // ✅ Reset reorder data
            }}
            reorderData={reorderData} // ✅ Pass reorder data
            onReorderComplete={() => setReorderData(null)} // ✅ Reset after completion
          />;
        
        case "conservative_care":
          return <AdvancedCareDashboard 
            source="conservative_care"
            initialView={reorderData ? 'newOrder' : 'providerSelection'} // ✅ Start at newOrder if reorder
            currentNavItem={currentNavItem}
            onContactModalOpen={handleOpenReviewRequest}
            onNavigateToPatients={() => {
              setCurrentNavItem('patients');
              setReorderData(null); // ✅ Reset reorder data
            }}
            reorderData={reorderData} // ✅ Pass reorder data
            onReorderComplete={() => setReorderData(null)} // ✅ Reset after completion
          />;
        
        default:
          return null;
      }
    }
    
    // ✅ If viewing patients list or dashboard, use SalesRepAdvancedCareDashboard
    if (currentNavItem === 'patients' || currentNavItem === 'pendingPartialOrders') {
      switch(selectedService) {
        case "skin_subs":
          return <SalesRepAdvancedCareDashboard 
            source="skin_subs" 
            currentNavItem={currentNavItem}
            onNavigateToPatients={() => {
              setCurrentNavItem('patients');
              setReorderData(null);
            }}
            onNavigateToAddPatient={(data) => {
              console.log('📝 Navigate to Add Patient with data:', data);
              
              if (data && data.skipToStep2) {
                setReorderData({
                  patientId: data.patientId,
                  patient: data.patient,
                  skipToStep2: true,
                  careType: data.careType,
                  providerId: data.providerId
                });
              }
              else if (data && data.isCompletingPartialOrder) {
                // ✅ NEW: Handle partial order completion
                setReorderData({
                  patientId: data.patientId,
                  patient: data.patient,
                  partialOrderId: data.partialOrderId,
                  isCompletingPartialOrder: true,
                  careType: data.careType,
                  providerId: data.providerId,
                  woundDetails: data.woundDetails,
                  skipToStep3: true, // ✅ New flag to go directly to step 3
                  proPacksIVRData: data.proPacksIVRData || null,
                });
              } 
               else {
                setReorderData(null);
              }
              
              setCurrentNavItem('addPatient');
            }}
            onNavigateToReorder={(data) => {
              console.log('🔄 Reorder triggered with data:', data);
              setReorderData(data);
              setCurrentNavItem('addPatient');
            }}
          />;

        case "conservative_care":
          return <SalesRepAdvancedCareDashboard 
            source="conservative_care"
            currentNavItem={currentNavItem}
            onNavigateToPatients={() => {
              setCurrentNavItem('patients');
              setReorderData(null);
            }}
            onNavigateToAddPatient={(data) => {
              console.log('📝 Navigate to Add Patient with data:', data);
              
              if (data && data.skipToStep2) {
                setReorderData({
                  patientId: data.patientId,
                  patient: data.patient,
                  skipToStep2: true,
                  careType: data.careType,
                  providerId: data.providerId
                });
              } else {
                setReorderData(null);
              }
              
              setCurrentNavItem('addPatient');
            }}
            onNavigateToReorder={(data) => {
              console.log('🔄 Reorder triggered with data:', data);
              setReorderData(data);
              setCurrentNavItem('addPatient');
            }}
          />;
        default:
          return null;
      }
    }
    
    // Otherwise show their stats dashboard
    return <SalesRepDashboard user={user} />;
  }
  
  // ✅ For sales reps without access - only show stats dashboard
  if (userRole === "sales_rep" && !salesRepHasAccess) {
    return (
      <>
        <div className="bg-amber-50 border-l-4 border-amber-500 p-4 m-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-amber-700">
                <strong className="font-semibold">Limited Access:</strong> You don't have access to detailed provider data yet. Ask your assigned providers to grant you access from their dashboard.
              </p>
            </div>
          </div>
        </div>
        <SalesRepDashboard user={user} />
      </>
    );
  }
  
  return null;
};

  // Handle navigation item clicks
  const handleNavClick = (item) => {
    if (item === 'support') {
      setIsSupportDropdownOpen(!isSupportDropdownOpen);
    } else if (item === 'patients') {
      setIsPatientsDropdownOpen(!isPatientsDropdownOpen);
      setCurrentNavItem('patients');
      setIsSupportDropdownOpen(false);
    } else if (item === 'reimbursementGuide' || item === 'faqs') {
      setCurrentNavItem(item);
      setIsSupportDropdownOpen(false);
    } else {
      setCurrentNavItem(item);
      setIsPatientsDropdownOpen(false);
      setIsSupportDropdownOpen(false);
    }
  };

  const handlePatientsHomeClick = () => {
    setIsPatientsDropdownOpen(!isPatientsDropdownOpen);
    
    // ✅ Different behavior for sales reps vs providers
    if (userRole === "sales_rep") {
      // For sales reps, clicking Patients (Home) shows their stats
      setCurrentNavItem(null); // Show SalesRepDashboard
    } else {
      // For providers, show patients list
      setCurrentNavItem('patients'); // Show patients list for providers
    }
    // Don't close sidebar on mobile when clicking Patients (Home) - allow dropdown to work
  };

  return (
    <FilterProvider>
      <div className="flex flex-col min-h-screen bg-gradient-to-br from-gray-50 via-teal-50/30 to-blue-50/30 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900 -mt-16">
        {/* MAIN LAYOUT: Left Nav + Main Content */}
        <div className="flex flex-col lg:flex-row flex-1 mt-16">
          {/* MOBILE SIDEBAR TOGGLE BUTTON - Only show on mobile when sidebar should be visible */}
          {shouldShowLeftNav && (
            <div className="lg:hidden fixed top-16 left-0 right-0 z-40 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm px-4 py-3 flex items-center justify-between">
              <button
                onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
                className="flex items-center space-x-2 text-gray-700 dark:text-gray-300 hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
              >
                {isMobileSidebarOpen ? (
                  <IoClose className="w-6 h-6" />
                ) : (
                  <IoMenu className="w-6 h-6" />
                )}
                <span className="font-semibold">Menu</span>
              </button>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                {userRole === "external_biller"
                  ? (billerSection === "guided" ? "Guided Orders" : "Compression Socks")
                  : currentNavItem === 'addPatient' ? 'Add New Patient' :
                    currentNavItem === 'pendingOrders' ? 'Pending Orders' :
                    currentNavItem === 'accountSummary' ? 'Account Summary' :
                    currentNavItem === 'reimbursementGuide' ? 'Reimbursement Guide' :
                    currentNavItem === 'faqs' ? 'FAQs' :
                    'Dashboard'}
              </h2>
            </div>
          )}

          {/* MOBILE SIDEBAR OVERLAY */}
          {shouldShowLeftNav && isMobileSidebarOpen && (
            <div
              className="lg:hidden fixed inset-0 bg-black/50 z-50 mt-16"
              onClick={() => setIsMobileSidebarOpen(false)}
            />
          )}

          {/* LEFT NAVIGATION - Show for providers OR sales reps with access */}
          {shouldShowLeftNav && (
            <aside className={`${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 fixed lg:static top-16 lg:top-0 bottom-0 left-0 h-[calc(100vh-4rem)] lg:h-auto w-64 bg-gradient-to-b from-gray-50 to-gray-100/50 dark:from-gray-800 dark:to-gray-800 border-r-2 border-teal-200/50 dark:border-teal-800/50 flex flex-col pt-10 shadow-sm z-50 lg:z-auto transition-transform duration-300 ease-in-out overflow-y-auto`}>
              {/* Navigation Items */}
              <nav className="flex-1 p-4 space-y-2">
                {userRole === "external_biller" && (
                  <>
                    <button
                      onClick={() => {
                        setBillerSection("guided");
                        setIsMobileSidebarOpen(false);
                      }}
                      className={`w-full px-4 py-3 rounded-lg transition-all text-left font-semibold ${
                        billerSection === "guided"
                          ? "bg-slate-700 text-white"
                          : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      }`}
                    >
                      Guided Orders
                    </button>
                    <button
                      onClick={() => {
                        setBillerSection("compression");
                        setIsMobileSidebarOpen(false);
                      }}
                      className={`w-full px-4 py-3 rounded-lg transition-all text-left font-semibold ${
                        billerSection === "compression"
                          ? "bg-slate-700 text-white"
                          : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      }`}
                    >
                      Compression Socks
                    </button>
                  </>
                )}
                {/* ✅ Only show Add New Patient for providers */}
                {(userRole === "provider" || (userRole === "sales_rep" && salesRepHasAccess)) &&  (
                  <>
                    <button
                      onClick={() => {
                        setReorderData(null);
                        handleNavClick('addPatient');
                        setIsMobileSidebarOpen(false);
                        
                      }}
                      className="w-full px-4 py-3 rounded-lg transition-all text-left font-semibold bg-gradient-to-r from-teal-600 to-teal-500 text-white hover:from-teal-700 hover:to-teal-600 shadow-md hover:shadow-lg flex items-center justify-between"
                    >
                      <span className="font-semibold">Add New Patient</span>
                      <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
                        <IoAdd className="text-teal-600 text-lg font-bold" />
                      </div>
                    </button>

                    {/* Divider Line */}
                    <div className="border-b border-gray-200 dark:border-gray-700 my-2"></div>
                  </>
                )}

                {(userRole === "provider" || (userRole === "sales_rep" && salesRepHasAccess)) && (
                  <button
                    onClick={() => {
                      handleNavClick('pendingPartialOrders');
                      setIsMobileSidebarOpen(false);
                    }}
                    className={`w-full px-4 py-3 rounded-lg transition-all text-left flex items-center justify-between ${
                      currentNavItem === 'pendingPartialOrders'
                        ? 'bg-slate-700 text-white'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <span className="font-semibold">Pending Orders</span>
                    
                    {readyToCompleteCount > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-teal-600 text-white text-xs font-bold">
                        {readyToCompleteCount}
                      </span>
                    )}
                  </button>
                )}

                {userRole === "provider" && (
                  <button
                    onClick={() => {
                      handleNavClick('deniedOrders');
                      setIsMobileSidebarOpen(false);
                    }}
                    className={`w-full px-4 py-3 rounded-lg transition-all text-left flex items-center justify-between ${
                      currentNavItem === 'deniedOrders'
                        ? 'bg-red-700 text-white'
                        : 'text-red-600 hover:bg-red-50'
                    }`}
                  >
                    <span className="font-semibold">Denied Orders</span>
                  </button>
                )}

                {/* Patients (Home) with Dropdown */}
                {userRole !== "external_biller" && (
                <div className="space-y-1">
                  <button
                    onClick={handlePatientsHomeClick}
                    className={`w-full px-4 py-3 rounded-lg transition-all text-left flex items-center justify-between ${
                      currentNavItem === 'patients' || currentNavItem === null
                        ? 'bg-slate-700 text-white'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <span className="font-semibold">Patients (Home)</span>
                    {isPatientsDropdownOpen ? (
                      <IoChevronUp className="text-lg" />
                    ) : (
                      <IoChevronDown className="text-lg" />
                    )}
                  </button>
                  
                  {/* Dropdown Sub-options */}
                  <AnimatePresence>
                    {isPatientsDropdownOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="pl-4 space-y-1">
                          <button
                            onClick={() => {
                              handleServiceSelect('skin_subs');
                              setIsMobileSidebarOpen(false);
                            }}
                            className={`w-full px-4 py-2 rounded-lg transition-all text-left text-sm ${
                              selectedService === 'skin_subs' && currentNavItem === 'patients'
                                ? 'bg-slate-600 text-white'
                                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                          >
                            Advanced Care
                          </button>
                          <button
                            onClick={() => {
                              handleServiceSelect('conservative_care');
                              setIsMobileSidebarOpen(false);
                            }}
                            className={`w-full px-4 py-2 rounded-lg transition-all text-left text-sm ${
                              selectedService === 'conservative_care' && currentNavItem === 'patients'
                                ? 'bg-slate-600 text-white'
                                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                          >
                            Conservative Care
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                )}
                {userRole !== "external_biller" && (
                <button
                  onClick={() => {
                    handleNavClick('accountSummary');
                    setIsMobileSidebarOpen(false);
                  }}
                  className={`w-full px-4 py-3 rounded-lg transition-all text-left ${
                    currentNavItem === 'accountSummary'
                      ? 'bg-slate-700 text-white'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <span className="font-semibold">Account Summary</span>
                </button>
                )}

                {userRole !== 'sales_rep' && userRole !== 'external_biller' && (
                  <div className="space-y-1">
                  <button
                    onClick={() => handleNavClick('support')}
                    className="w-full px-4 py-3 rounded-lg transition-all text-left flex items-center justify-between text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <span className="font-semibold">Support</span>
                    {isSupportDropdownOpen ? (
                      <IoChevronUp className="text-lg" />
                    ) : (
                      <IoChevronDown className="text-lg" />
                    )}
                  </button>
                  
                  {/* Dropdown Sub-options */}
                  <AnimatePresence>
                    {isSupportDropdownOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="pl-4 space-y-1">
                          <button
                            onClick={() => {
                              handleNavClick('reimbursementGuide');
                              setIsMobileSidebarOpen(false);
                            }}
                            className={`w-full px-4 py-2 rounded-lg transition-all text-left text-sm ${
                              currentNavItem === 'reimbursementGuide'
                                ? 'bg-slate-600 text-white'
                                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                          >
                            Reimbursement Guide
                          </button>
                          <button
                            onClick={() => {
                              handleNavClick('faqs');
                              setIsMobileSidebarOpen(false);
                            }}
                            className={`w-full px-4 py-2 rounded-lg transition-all text-left text-sm ${
                              currentNavItem === 'faqs'
                                ? 'bg-slate-600 text-white'
                                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                          >
                            FAQs
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                )}
                {/* Support with Dropdown */}
                
              </nav>
              
              {/* Need Help Section */}
              { userRole !== 'sales_rep' && userRole !== 'external_biller' && (
                <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                <div className="bg-gray-100 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                  <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2">Need Help?</h3>
                  <p className="text-gray-700 dark:text-gray-300 text-xs mb-4">
                    Contact your Representative
                  </p>
                  
                  {/* Contact Number */}
                  <div className="flex items-center mb-4">
                    <IoCallOutline className="text-sm text-gray-700 dark:text-gray-300 mr-1.5" />
                    <a 
                      href="tel:8883380490" 
                      className="text-sm font-semibold text-gray-900 dark:text-white hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                    >
                      888.338.0490
                    </a>
                  </div>

                  {/* Message Representative Button */}
                  <button
                    onClick={() => setOpenModal(true)}
                    className="w-full px-3 py-2 bg-slate-700 dark:bg-slate-600 hover:bg-slate-800 dark:hover:bg-slate-700 text-white rounded-md font-semibold text-xs shadow-sm hover:shadow transition-all"
                  >
                    Message Representative
                  </button>
                </div>
              </div>
              )}
              
                
              
            </aside>
          )}

          {/* MAIN CONTENT AREA */}
          <main className={`${shouldShowLeftNav ? "flex-1" : "w-full"} overflow-y-auto bg-gradient-to-br from-white via-gray-50/50 to-teal-50/30 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900 scroll-smooth pt-20 lg:pt-4 px-4 lg:px-6`}>
            {userRole === "provider" ? (
              <>
                <DashboardTour />
                <ContactModal open={openModal} onClose={() => setOpenModal(false)} />
                <AnimatePresence mode="wait">
                  {isTransitioning ? (
                    <motion.div
                      key="loading"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="flex items-center justify-center min-h-[400px]"
                    >
                      <div className="text-center">
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          className="w-12 h-12 border-4 border-slate-200 border-t-slate-700 rounded-full mx-auto mb-4"
                        />
                        <p className="text-gray-600 dark:text-gray-400 font-semibold">
                          Switching to {nextService === "skin_subs" ? "Advanced Care" : nextService === "conservative_care" ? "Conservative Care" : ""}...
                        </p>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      key={selectedService}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      {renderMainContent()}
                    </motion.div>
                  )}
                </AnimatePresence>
              </>
            ) : userRole === "sales_rep" ? (
              checkingAccess ? (
                <div className="flex justify-center items-center min-h-screen">
                  <div className="text-center">
                    <PropagateLoader color={"#10B981"} loading={true} size={15} />
                    <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
                      Checking access permissions...
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  {renderMainContent()}
                </>
              )
            ) : userRole === "admin" || userRole === "ceo" ? (
  // ✅ Check if it's the special shipping admin
  userEmail === 'Katy@promedhealthplus.com' || userEmail === 'Manohar@promedhealthplus.com' ? (
    renderMainContent()
  ): (
              <div className="text-center px-6 py-12">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                  Admin Dashboard
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Advanced admin features coming soon!
                </p>
                <div className="inline-flex items-center space-x-2 text-teal-600 dark:text-teal-400">
                  <span className="animate-pulse">⚡</span>
                  <span className="font-semibold">In Development</span>
                  <span className="animate-pulse">⚡</span>
                </div>
              </div>
            )) :  userEmail === "Katy@promedhealthplus.com" ? (
  renderMainContent()
) : userRole === "external_biller" || userRole === "socks_biller" ? (
  renderMainContent()) : (
              <div className="text-center px-6 py-12">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Unknown Role: {userRole || "No Role Found"}
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Please contact support if this issue persists.
                </p>
                <button
                  onClick={() => {
                    localStorage.clear();
                    window.location.href = "/login";
                  }}
                  className="px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
                >
                  Logout & Try Again
                </button>
              </div>
            )}

            {/* Contact Modal for chatbot */}
            <ContactModal open={openModal} onClose={() => setOpenModal(false)} />
          </main>
        </div>
      </div>
      <RequestReviewModal
      open={reviewRequestModalOpen}
      onClose={handleCloseReviewRequest}
      patient={selectedPatientForReview}
      onSuccess={handleReviewRequestSuccess}
    />
    </FilterProvider>
  );
};

export default Dashboard;